import { Origin, SyncStatusEnum } from "../../models/types/general-types";
import { AccountView } from "../../models/view/account/account.view";
import { TestLog } from "./test.log";
import { inject, Injectable } from "@angular/core";
import { BasiqSync } from "@bitwarden/web-vault/app/services/syncing/basiq/sync-levels/basiq.sync";
import { BasiqIoTransactionType } from "../../models/types/basiq-io-transaction.types";
import { BasiqAccountType, BasiqInstitution } from "../../models/types/basiq.types";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { OriginImportData } from "@bitwarden/web-vault/app/services/syncing/gloss.sync";
import { PlaidSync } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.sync";
import { FiskilSync } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.sync";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";

export type BasiqSyncType = "account" | "transaction" | "institution";

export type SyncResult = {
  type: BasiqSyncType;
  error?: any;
  transactions?: BasiqIoTransactionType[];
  accounts?: BasiqAccountType[];
  institutions?: BasiqInstitution[];
};

export type Syncer = BasiqSync | PlaidSync | FiskilSync;

@Injectable({
  providedIn: "root",
})
export class GlossSyncFactory {
  private testLogService: TestLog = new TestLog(inject(LogService));
  private basiqSync = inject(BasiqSync);
  private plaidSync = inject(PlaidSync);
  private fiskilSync = inject(FiskilSync);
  private glossSyncService = inject(GlossSyncService);

  /** Connects to the syncer aka connector such as Basiq, Plaid, etc...*/
  async connect(origin: Origin, isFromGuide: boolean = false) {
    try {
      const syncer = this.getSyncer(origin);
      await syncer.connect(isFromGuide);
    } catch (error) {
      await this.syncError(error, origin, []);
    }
  }

  /** Import: for the first time interaction */
  async import(origin: Origin, originImportData: OriginImportData) {
    try {
      const syncer = this.getSyncer(origin);
      await syncer.import(originImportData);
    } catch (error) {
      await this.syncError(error, origin, []);
    }
  }

  /** sync: For the consecutive interactions after first (import) one */
  async sync(origin: Origin, accounts: AccountView[]) {
    try {
      const syncer = this.getSyncer(origin);
      await syncer.sync(accounts);
    } catch (error) {
      await this.syncError(error, origin, accounts);
    }
  }

  /** Use the proper syncer(Basiq?, Plaid?) to let users connect their additional accounts from the insto  */
  async connectToInstitution(origin: Origin, institution: InstitutionView) {
    try {
      const syncer = this.getSyncer(origin);
      await syncer.connectToInstitution(institution);
    } catch (error) {
      await this.syncError(error, origin, []);
    }
  }

  /** Use the proper syncer(Basiq?, Plaid?) to let users connect their additional accounts from the insto  */
  async reconnectToInstitution(origin: Origin, institution: InstitutionView) {
    try {
      const syncer = this.getSyncer(origin);
      await syncer.reconnectToInstitution(institution);
    } catch (error) {
      await this.syncError(error, origin, []);
    }
  }

  async addPlaidAccounts(institution: InstitutionView): Promise<void> {
    await this.plaidSync.addAccounts(institution);
  }

  private getSyncer(origin: Origin): Syncer {
    if (origin === Origin.basiq) {
      return this.basiqSync;
    }

    if (origin === Origin.plaid) {
      return this.plaidSync;
    }

    if (origin === Origin.fiskil) {
      return this.fiskilSync;
    }
  }

  private async syncError(error: any, origin: Origin, accounts: AccountView[]) {
    this.testLogService.testLog({
      error: error,
      level: `Starting ${origin} sync`,
    });
    await this.glossSyncService.terminateSync(accounts, SyncStatusEnum.unknownError);
  }
}
